import { AnyAction } from 'redux';
import { FormikErrors } from 'formik';
import { HandledError } from '@synergy/api';
import { Media } from '@synergy/models';
import lodashSet from 'lodash/set';

import { AddressFormValues } from '../../../../models/address-form-values';
import { BankAccountErrorCode, BankAccountErrorCodeMessageId } from '../../../../models/bank-account-error-code';
import { BankAccountFormValues } from '../../../../models/bank-account-form-values';
import {
    CONTACT_DETAIL_BANK_ACCOUNT_CREATED,
    CONTACT_DETAIL_BANK_ACCOUNT_DELETE_FINISHED,
    CONTACT_DETAIL_BANK_ACCOUNT_ERROR,
    CONTACT_DETAIL_BANK_ACCOUNT_UPDATED,
    CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED,
    CONTACT_DETAIL_OVERVIEW_ERROR,
    CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_REMOVED,
    CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD_FINISHED,
    CONTACT_DETAIL_OVERVIEW_RECEIVE_DETAILS,
    CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_RECEIVED,
    CONTACT_DETAIL_OVERVIEW_UPDATE,
} from './contact-detail-overview-actions';
import { ContactDetailOverviewFormValues } from '../models/contact-detail-overview-form-values';
import { EmailErrorCode, EmailErrorCodeMessageId } from '../../../../models/email-error-code';
import { EmbeddedContact } from '../../../../../bonus-settlements/models/embedded-contact';
import { RelatedAccount } from '../models/related-account';

export interface ContactDetailOverviewReducerState {
    contact?: EmbeddedContact;
    bankAccounts: BankAccountFormValues[];
    relatedAccounts: RelatedAccount[];
    isValid: boolean;
    isDirty: boolean;
    overviewErrors: FormikErrors<ContactDetailOverviewFormValues>;
    bankAccountErrors: FormikErrors<BankAccountFormValues>;
    /**
     * The contact as the server last handed it over (#2159). `contact` cannot
     * serve: leaving a tab parks its input right there.
     */
    baseline?: EmbeddedContact;
}

const initialContactReducerState: ContactDetailOverviewReducerState = {
    bankAccounts: [],
    relatedAccounts: [],
    isValid: true,
    isDirty: false,
    overviewErrors: {},
    bankAccountErrors: {},
};

export const contactDetailOverviewReducer = (
    state: ContactDetailOverviewReducerState = initialContactReducerState,
    action: AnyAction
): ContactDetailOverviewReducerState => {
    switch (action.type) {
        case CONTACT_DETAIL_OVERVIEW_RECEIVE_DETAILS: {
            return {
                ...state,
                contact: action['contact'],
                // The same values, kept apart from the ones the tabs edit.
                baseline: action['contact'],
                bankAccounts: action['contact'].bankAccounts,
                isDirty: false,
                overviewErrors: {},
            };
        }

        case CONTACT_DETAIL_OVERVIEW_ERROR: {
            const formErrors: FormikErrors<ContactDetailOverviewFormValues> = { ...state.overviewErrors };

            (action['errorData'] as HandledError<EmailErrorCode>[])?.forEach(error => {
                lodashSet(formErrors, error.property, EmailErrorCodeMessageId[error.error]);
            });

            return {
                ...state,
                isValid: false,
                overviewErrors: formErrors,
            };
        }

        case CONTACT_DETAIL_BANK_ACCOUNT_ERROR: {
            const formErrors: FormikErrors<BankAccountFormValues> = { ...state.bankAccountErrors };
            (action['errorData'] as HandledError<BankAccountErrorCode>[])?.forEach(error => {
                lodashSet(formErrors, error.property, BankAccountErrorCodeMessageId[error.error]);
            });

            return {
                ...state,
                bankAccountErrors: formErrors,
            };
        }

        case CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED: {
            const media: Media = action['media'];
            const duplicatedBankAccounts = [...state.bankAccounts];
            const index = duplicatedBankAccounts.findIndex(account => account.id === action['bankAccountId']);
            if (index >= 0) {
                duplicatedBankAccounts[index].media = [...duplicatedBankAccounts[index].media, media];
            }

            return {
                ...state,
                bankAccounts: duplicatedBankAccounts,
            };
        }

        case CONTACT_DETAIL_BANK_ACCOUNT_DELETE_FINISHED: {
            return {
                ...state,
                bankAccounts: state.bankAccounts.filter(bankAccount => bankAccount.id !== action['id']),
                bankAccountErrors: {},
            };
        }

        case CONTACT_DETAIL_BANK_ACCOUNT_UPDATED: {
            const duplicatedBankAccounts = [...state.bankAccounts];
            const index = duplicatedBankAccounts.findIndex(account => account.id === action['bankAccount'].id);

            if (index >= 0) {
                duplicatedBankAccounts[index] = action['bankAccount'];
            }

            return {
                ...state,
                bankAccounts: duplicatedBankAccounts,
                bankAccountErrors: {},
            };
        }

        case CONTACT_DETAIL_BANK_ACCOUNT_CREATED: {
            return {
                ...state,
                bankAccounts: [...state.bankAccounts, action['newBankAccount']],
                bankAccountErrors: {},
            };
        }

        case CONTACT_DETAIL_OVERVIEW_UPDATE: {
            const { formData, isDirty, isValid, errors } = action;

            const nextContact = {
                ...state.contact,
                ...formData,
                addresses: formData.addresses.map((address: AddressFormValues) => ({
                    ...address,
                    country: address.country?.code,
                    regionCode: address.region?.code,
                })),
            };

            return {
                ...state,
                contact: nextContact,
                isDirty: state.isDirty || isDirty,
                isValid,
                overviewErrors: errors,
            };
        }

        case CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD_FINISHED: {
            if (!state.contact) {
                return state;
            }

            const duplicatedContact = { ...state.contact };
            duplicatedContact.image = (action['media'] as Media).pathname;

            return {
                ...state,
                contact: duplicatedContact,
                // The picture is already on the server; without this the mask
                // would report itself unsaved for the rest of the session.
                baseline: state.baseline ? { ...state.baseline, image: duplicatedContact.image } : state.baseline,
            };
        }

        case CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_REMOVED: {
            if (!state.contact) {
                return state;
            }

            return {
                ...state,
                baseline: state.baseline ? { ...state.baseline, image: undefined } : state.baseline,
                contact: {
                    ...state.contact,
                    image: undefined,
                },
            };
        }

        case CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_RECEIVED: {
            return {
                ...state,
                relatedAccounts: action['relatedAccounts'],
            };
        }

        default: {
            return state;
        }
    }
};
